import React, { useState, useEffect, useMemo } from 'react';
import { Search, X, IndianRupee, Save, RotateCcw, Info, Tag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import ErrorPage from '../../components/ErrorPage';
import { getFeaturePrices, saveFeaturePrices, clearError } from '../../redux/masterSlice';
import { getFeatureCatalogue } from '../../redux/tenantSlice';

// Currency is INR and fixed — the platform sells in one currency, so there is no
// currency column and no per-row selector to keep in sync with anything.
const INR = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
});

const MAX_SEARCH_LENGTH = 50;

// Money in, money out: keep the cell value as the STRING the user typed so a
// half-finished "1200." doesn't get parsed and snapped back under their cursor.
// Negatives are impossible (the sign is stripped, not validated away afterwards)
// and the fraction is capped at 2 places.
const sanitizePrice = (raw) => {
    const cleaned = String(raw ?? '').replace(/[^\d.]/g, '');
    const [whole, ...rest] = cleaned.split('.');
    const head = whole.slice(0, 9);
    if (rest.length === 0) return head;
    return `${head}.${rest.join('').slice(0, 2)}`;
};

const toNumber = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) && n > 0 ? n : 0;
};

// Module level on purpose: declared inside FeaturePrice it would be a NEW
// component type on every render, so React would unmount and remount the input
// on each keystroke and the cell would lose focus after one character.
const PriceInput = ({ value, dirty, onChange, onBlur, testid }) => (
    <div className="relative">
        <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        <input
            type="text"
            inputMode="decimal"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            className={`w-28 pl-7 pr-2 py-1.5 text-right text-[13px] font-semibold border-2 rounded-lg focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all ${dirty ? 'border-[#D7A05C] bg-[#D7A05C]/5 text-[#7A5320]' : 'border-gray-200 text-gray-700'}`}
            data-testid={testid}
        />
    </div>
);

const FeaturePrice = () => {
    const { t } = useTranslation('featurepricemaster');
    const dispatch = useDispatch();

    const { featurePrices, featurePriceLoading, featurePriceSaveLoading, error } = useSelector((state) => state.master);

    // Read-only borrow of the tenant slice: the feature list and its grouping are
    // the tenant-creation catalogue. A local copy here would be a second source
    // of truth that silently misses any feature added to the platform.
    const featureGroups = useSelector((state) => state.tenant?.featureGroups);

    const [searchQuery, setSearchQuery] = useState('');
    const [draft, setDraft] = useState({}); // featureId -> { monthlyPrice, yearlyPrice } as strings

    useEffect(() => {
        dispatch(getFeatureCatalogue());
        dispatch(getFeaturePrices());
    }, [dispatch]);

    // Every feature in the catalogue, flattened once, with its group for headers.
    const groups = useMemo(() => (Array.isArray(featureGroups) ? featureGroups : []), [featureGroups]);

    const allFeatures = useMemo(
        () => groups.flatMap((g) => (g.features || []).map((f) => ({ ...f, groupId: g.id, groupLabel: g.label }))),
        [groups]
    );

    // What the server currently holds — the baseline every dirty check is against.
    const baseline = useMemo(() => {
        const map = {};
        (featurePrices || []).forEach((p) => {
            map[p.featureId] = {
                monthlyPrice: toNumber(p.monthlyPrice),
                yearlyPrice: toNumber(p.yearlyPrice)
            };
        });
        return map;
    }, [featurePrices]);

    // Reset the editable copy whenever the saved prices change — on first load and
    // again after a successful save, so "saved" always means "matches the server".
    // A feature with no saved row starts at 0 rather than blank.
    useEffect(() => {
        const next = {};
        allFeatures.forEach((f) => {
            next[f.id] = {
                monthlyPrice: String(baseline[f.id]?.monthlyPrice ?? 0),
                yearlyPrice: String(baseline[f.id]?.yearlyPrice ?? 0)
            };
        });
        setDraft(next);
    }, [allFeatures, baseline]);

    const isRowDirty = (featureId) => {
        const row = draft[featureId];
        if (!row) return false;
        const base = baseline[featureId] || { monthlyPrice: 0, yearlyPrice: 0 };
        return toNumber(row.monthlyPrice) !== base.monthlyPrice || toNumber(row.yearlyPrice) !== base.yearlyPrice;
    };

    const dirtyIds = useMemo(
        () => allFeatures.map((f) => f.id).filter(isRowDirty),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [allFeatures, draft, baseline]
    );

    // Totals span EVERY priced feature, not the filtered view — this is the
    // Super Admin's sanity check on the whole price list, so hiding rows behind
    // a search must not change the number.
    const totals = useMemo(() => {
        let monthly = 0;
        let yearly = 0;
        allFeatures.forEach((f) => {
            monthly += toNumber(draft[f.id]?.monthlyPrice);
            yearly += toNumber(draft[f.id]?.yearlyPrice);
        });
        return { monthly, yearly, priced: allFeatures.filter((f) => toNumber(draft[f.id]?.monthlyPrice) > 0 || toNumber(draft[f.id]?.yearlyPrice) > 0).length };
    }, [allFeatures, draft]);

    const filteredGroups = useMemo(() => {
        const needle = searchQuery.trim().toLowerCase();
        return groups
            .map((g) => ({
                ...g,
                features: (g.features || []).filter((f) =>
                    !needle ||
                    (f.label || '').toLowerCase().includes(needle) ||
                    (f.id || '').toLowerCase().includes(needle)
                )
            }))
            .filter((g) => g.features.length > 0);
    }, [groups, searchQuery]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value.replace(/[<>]/g, '').slice(0, MAX_SEARCH_LENGTH));
    };

    const handlePriceChange = (featureId, field, value) => {
        const clean = sanitizePrice(value);
        setDraft((prev) => ({
            ...prev,
            [featureId]: { ...(prev[featureId] || { monthlyPrice: '0', yearlyPrice: '0' }), [field]: clean }
        }));
    };

    // Blur normalises an empty / trailing-dot cell back to a real number so the
    // row's dirty state can't be decided by a string the user can't see.
    const handlePriceBlur = (featureId, field) => {
        setDraft((prev) => ({
            ...prev,
            [featureId]: { ...(prev[featureId] || {}), [field]: String(toNumber(prev[featureId]?.[field])) }
        }));
    };

    const handleDiscard = () => {
        const next = {};
        allFeatures.forEach((f) => {
            next[f.id] = {
                monthlyPrice: String(baseline[f.id]?.monthlyPrice ?? 0),
                yearlyPrice: String(baseline[f.id]?.yearlyPrice ?? 0)
            };
        });
        setDraft(next);
    };

    const handleSave = async () => {
        if (dirtyIds.length === 0) return;
        // ONLY the edited rows. The endpoint upserts per feature id and answers
        // with the complete list, so an untouched row keeps its stored price
        // whether or not we resend it — and resending all ~50 would cost a write
        // per feature on every save to rewrite values that never changed.
        const prices = dirtyIds.map((id) => ({
            featureId: id,
            monthlyPrice: toNumber(draft[id]?.monthlyPrice),
            yearlyPrice: toNumber(draft[id]?.yearlyPrice)
        }));
        try {
            await dispatch(saveFeaturePrices({ prices })).unwrap();
            toast.success(t('featurepricemaster.saved_success', 'Feature prices saved.'));
        } catch (err) {
            dispatch(clearError());
            toast.warn(err?.error || t('featurepricemaster.save_failed', 'Failed to save feature prices.'));
        }
    };

    const isLoading = featurePriceLoading || (groups.length === 0 && !error);

    // Only blank the page when there is nothing to render at all — a failed save
    // is reported by its own toast and must not take the table away.
    if (error && allFeatures.length === 0 && !featurePriceLoading) {
        return <ErrorPage error={error} pageName={t('featurepricemaster.page_name', 'Feature Price Management')} />;
    }

    return (
        <div className="w-full" data-testid="feature-price-master-container">
            <div className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4" data-testid="feature-price-master-header">
                    <h1 className="text-2xl sm:text-3xl pb-2 font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent ml-2" data-testid="feature-price-master-title">
                        {t('featurepricemaster.feature_price_management', 'Feature Price Management')}
                    </h1>
                    <div className="relative group w-full sm:w-72" data-testid="feature-price-master-search-container">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#4ABDBD] transition-colors" size={20} />
                        <input
                            type="text"
                            placeholder={t('featurepricemaster.search_placeholder', 'Search features...')}
                            value={searchQuery}
                            onChange={handleSearchChange}
                            maxLength={MAX_SEARCH_LENGTH}
                            className="w-full pl-11 pr-9 py-2 bg-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4ABDBD] focus:border-[#4ABDBD] focus:bg-white transition-all duration-300 text-gray-700 placeholder-gray-400"
                            data-testid="feature-price-master-search-input"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                                data-testid="feature-price-master-search-clear"
                            >
                                <X size={16} />
                            </button>
                        )}
                    </div>
                </div>

                <div className="mb-4 rounded-xl px-3 py-2.5 text-[11.5px] flex items-start gap-2 bg-[#E7F5F4] text-[#2D8A8A]" data-testid="feature-price-master-hint">
                    <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                        {t('featurepricemaster.page_hint', 'The platform price list, in Indian Rupees. Every feature in the tenant-creation catalogue gets a monthly and a yearly price; a feature left at 0 is simply not charged for. Nothing is saved until you press Save prices.')}
                    </span>
                </div>

                {/* Totals — sanity check over the whole list */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5" data-testid="feature-price-master-summary">
                    <div className="rounded-xl border border-gray-200 px-4 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{t('featurepricemaster.total_monthly', 'Total / month')}</p>
                        <p className="text-lg font-bold text-[#2D8A8A]" data-testid="feature-price-master-total-monthly">{INR.format(totals.monthly)}</p>
                    </div>
                    <div className="rounded-xl border border-gray-200 px-4 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{t('featurepricemaster.total_yearly', 'Total / year')}</p>
                        <p className="text-lg font-bold text-[#2D8A8A]" data-testid="feature-price-master-total-yearly">{INR.format(totals.yearly)}</p>
                    </div>
                    <div className="rounded-xl border border-gray-200 px-4 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{t('featurepricemaster.priced_features', 'Priced features')}</p>
                        <p className="text-lg font-bold text-gray-700" data-testid="feature-price-master-priced-count">
                            {totals.priced}<span className="text-sm font-semibold text-gray-400"> / {allFeatures.length}</span>
                        </p>
                    </div>
                </div>

                {/* Table */}
                {isLoading ? (
                    <div className="flex items-center justify-center py-16 sm:py-20" data-testid="feature-price-master-loading-container">
                        <div className="flex flex-col items-center gap-4">
                            <div className="border-4 border-[#4ABDBD] border-t-transparent rounded-full w-12 h-12 animate-spin"></div>
                            <p className="text-gray-500 text-sm">{t('featurepricemaster.loading_prices', 'Loading feature prices...')}</p>
                        </div>
                    </div>
                ) : filteredGroups.length === 0 ? (
                    <div className="text-center py-16 sm:py-20" data-testid="feature-price-master-empty-container">
                        <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full mb-6">
                            <Tag size={48} className="text-gray-400" />
                        </div>
                        <h3 className="text-xl sm:text-2xl font-semibold text-gray-700 mb-2">
                            {searchQuery
                                ? t('featurepricemaster.no_features_found', 'No features found')
                                : t('featurepricemaster.no_features_yet', 'No features in the catalogue')}
                        </h3>
                        <p className="text-gray-500 max-w-md mx-auto">
                            {searchQuery
                                ? t('featurepricemaster.try_adjusting_search', 'Try adjusting your search.')
                                : t('featurepricemaster.catalogue_empty_hint', 'The feature catalogue could not be loaded, so there is nothing to price yet.')}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto rounded-2xl border border-gray-200" data-testid="feature-price-master-table-container">
                        <table className="w-full min-w-[560px] border-collapse" data-testid="feature-price-master-table">
                            <thead>
                                <tr className="bg-[#F7FBFA]">
                                    <th className="text-left text-[11px] font-bold uppercase tracking-wide text-gray-500 px-4 py-3">
                                        {t('featurepricemaster.col_feature', 'Feature')}
                                    </th>
                                    <th className="text-right text-[11px] font-bold uppercase tracking-wide text-gray-500 px-4 py-3 w-40">
                                        {t('featurepricemaster.col_monthly', 'Price / month (₹)')}
                                    </th>
                                    <th className="text-right text-[11px] font-bold uppercase tracking-wide text-gray-500 px-4 py-3 w-40">
                                        {t('featurepricemaster.col_yearly', 'Price / year (₹)')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredGroups.map((group) => (
                                    <React.Fragment key={group.id}>
                                        <tr className="bg-gray-50 border-t border-gray-200" data-testid={`feature-price-master-group-${group.id}`}>
                                            <td colSpan={3} className="px-4 py-2">
                                                <span className="text-[12px] font-bold text-[#1a365d]">{group.label}</span>
                                                <span className="ml-2 text-[11px] text-gray-400">
                                                    {/* `num`, not `count` — `count` would send i18next looking for
                                                        plural-suffixed keys that this flat bundle doesn't carry. */}
                                                    {t('featurepricemaster.group_count', { num: group.features.length, defaultValue: `${group.features.length} feature(s)` })}
                                                </span>
                                            </td>
                                        </tr>
                                        {group.features.map((f) => (
                                            <tr
                                                key={f.id}
                                                className={`border-t border-gray-100 transition-colors ${isRowDirty(f.id) ? 'bg-[#D7A05C]/5' : 'hover:bg-[#F7FBFA]'}`}
                                                data-testid={`feature-price-master-row-${f.id}`}
                                            >
                                                <td className="px-4 py-2.5 align-top">
                                                    <span className="block text-[13px] font-semibold text-gray-800">
                                                        {f.label}
                                                        {f.core && (
                                                            <span className="ml-2 px-1.5 py-0.5 rounded bg-[#4ABDBD]/10 text-[#2D8A8A] text-[10px] font-bold align-middle">
                                                                {t('featurepricemaster.core', 'core')}
                                                            </span>
                                                        )}
                                                    </span>
                                                    {f.description && (
                                                        <span className="block text-[11px] text-gray-500 leading-snug mt-0.5">{f.description}</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2.5 text-right align-top">
                                                    <div className="flex justify-end">
                                                        <PriceInput
                                                            value={draft[f.id]?.monthlyPrice ?? '0'}
                                                            dirty={isRowDirty(f.id)}
                                                            onChange={(v) => handlePriceChange(f.id, 'monthlyPrice', v)}
                                                            onBlur={() => handlePriceBlur(f.id, 'monthlyPrice')}
                                                            testid={`feature-price-master-monthly-${f.id}`}
                                                        />
                                                    </div>
                                                </td>
                                                <td className="px-4 py-2.5 text-right align-top">
                                                    <div className="flex justify-end">
                                                        <PriceInput
                                                            value={draft[f.id]?.yearlyPrice ?? '0'}
                                                            dirty={isRowDirty(f.id)}
                                                            onChange={(v) => handlePriceChange(f.id, 'yearlyPrice', v)}
                                                            onBlur={() => handlePriceBlur(f.id, 'yearlyPrice')}
                                                            testid={`feature-price-master-yearly-${f.id}`}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Sticky save bar — manual save only; typing never writes. */}
                <div className="sticky bottom-0 mt-4 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-white/95 backdrop-blur border-t border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="feature-price-master-save-bar">
                    <p className="text-[12px] text-gray-500 flex-1" data-testid="feature-price-master-dirty-count">
                        {dirtyIds.length === 0
                            ? t('featurepricemaster.no_changes', 'No unsaved changes')
                            : t('featurepricemaster.edited_rows', { num: dirtyIds.length, defaultValue: `${dirtyIds.length} row(s) edited` })}
                    </p>
                    <button
                        onClick={handleDiscard}
                        disabled={dirtyIds.length === 0 || featurePriceSaveLoading}
                        className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                        data-testid="feature-price-master-discard-button"
                    >
                        <RotateCcw size={16} />
                        {t('featurepricemaster.discard', 'Discard changes')}
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={dirtyIds.length === 0 || featurePriceSaveLoading}
                        className="flex items-center justify-center gap-2 px-5 py-2 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all font-medium text-sm shadow-md disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                        data-testid="feature-price-master-save-button"
                    >
                        {featurePriceSaveLoading
                            ? <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                            : <Save size={16} />}
                        {featurePriceSaveLoading
                            ? t('featurepricemaster.saving', 'Saving...')
                            : t('featurepricemaster.save_prices', 'Save prices')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FeaturePrice;
